import React, { useState, useMemo, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import type { CoatingRow } from './PumpHouseCoatingTable';
import {
  type TemperatureLimits,
  type DailyJoint,
  // FUTURE: Top fault reasons card
  // type FaultEntry,
  // FUTURE: Environmental window, material, operator leaderboard
  // type EnvEntry,
  // type OperatorEntry,
  DEFAULT_LIMITS,
  computeDashData,
  // environmentalExcursionCopy,
} from './dashboardCalculations';

// ─── design tokens ────────────────────────────────────────────────────────────

const C = {
  blue:    '#00273F',
  red:     '#EB1C37',
  green:   '#1F9A3A',
  yellow:  '#FFCA57',
  grey:    '#969696',
  dkRed:   '#C4001F',
  bg:      '#F5F6F8',
  border:  '#E2E5EA',
  surface: '#FFFFFF',
  surf2:   '#EEF0F3',
  text:    '#464646',
  warnBg:  '#FFF6DE',
  warnTxt: '#B8860B',
};

// ─── shared prop types ────────────────────────────────────────────────────────

interface InfoContent { title: string; description: string; tip?: string; }

// ─── InfoPopover ──────────────────────────────────────────────────────────────

const InfoPopover: React.FC<InfoContent> = ({ title, description, tip }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        aria-label={`Info: ${title}`}
        style={{
          width: 20, height: 20, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: open ? C.blue : C.surf2, color: open ? '#fff' : C.grey,
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
        }}
      >
        <HelpCircle style={{ width: 11, height: 11 }} strokeWidth={2.5} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 280, zIndex: 50,
          background: C.surface, border: `0.5px solid ${C.border}`, borderRadius: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)', overflow: 'hidden',
        }}>
          <div style={{ height: 3, background: C.blue }} />
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.blue, marginBottom: 6 }}>{title}</div>
            <p style={{ fontSize: 12, color: C.text, lineHeight: 1.6, margin: 0 }}>{description}</p>
            {tip && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.surf2}`, display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 11, color: C.yellow, flexShrink: 0, marginTop: 1 }}>●</span>
                <p style={{ fontSize: 11, color: C.grey, lineHeight: 1.5, margin: 0, fontStyle: 'italic' }}>{tip}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── KpiCard ──────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  delta?: string;
  deltaType?: 'up' | 'down' | 'warn' | 'neutral';
  info?: InfoContent;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, delta, deltaType = 'neutral', info }) => {
  const deltaColor = { up: C.green, down: C.dkRed, warn: C.warnTxt, neutral: C.grey }[deltaType];
  return (
    <div
      style={{
        background: '#fff', border: '1px solid #E5E7EB',
        borderRadius: 8, padding: '14px 16px', position: 'relative',
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
      }}
    >
      {info && <div style={{ position: 'absolute', top: 8, right: 8 }}><InfoPopover {...info} /></div>}
      <div style={{ fontSize: 11, fontWeight: 500, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', paddingRight: info ? 24 : 0 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginTop: 4, letterSpacing: '-0.01em' }}>
        {value}
      </div>
      {delta && (
        <div style={{ fontSize: 11, marginTop: 4, color: deltaColor }}>
          {deltaType === 'up' && '▲ '}{deltaType === 'down' && '▼ '}{delta}
        </div>
      )}
    </div>
  );
};

// ─── CardHeader ───────────────────────────────────────────────────────────────

interface CardHeaderProps { title: string; subtitle?: string; info?: InfoContent; }

const CardHeader: React.FC<CardHeaderProps> = ({ title, subtitle, info }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{subtitle}</div>}
    </div>
    {info && <InfoPopover {...info} />}
  </div>
);

// ─── Card ─────────────────────────────────────────────────────────────────────

interface CardProps { children: React.ReactNode; style?: React.CSSProperties; }

const Card: React.FC<CardProps> = ({ children, style: extra }) => (
  <div
    style={{
      background: '#fff', border: '1px solid #E5E7EB',
      borderRadius: 8, padding: '16px',
      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)', ...extra,
    }}
  >
    {children}
  </div>
);

// ─── KeyValue row ─────────────────────────────────────────────────────────────

interface KVProps { label: string; value: React.ReactNode; valueStyle?: React.CSSProperties; }

const KV: React.FC<KVProps> = ({ label, value, valueStyle }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', color: C.text, borderBottom: `0.5px solid ${C.surf2}` }}>
    <span>{label}</span>
    <strong style={{ fontWeight: 400, color: C.blue, ...valueStyle }}>{value}</strong>
  </div>
);

// ─── Chart tooltip ────────────────────────────────────────────────────────────

const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.blue, borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
          {p.name}: {p.value}
        </div>
      ))}
    </div>
  );
};

// ─── LimitsPanel ─────────────────────────────────────────────────────────────

interface LimitsPanelProps {
  draft: TemperatureLimits;
  onChange: (l: TemperatureLimits) => void;
  onApply: () => void;
  onClear: () => void;
  savedMsg: boolean;
}

const LimitsPanel: React.FC<LimitsPanelProps> = ({ draft, onChange, onApply, onClear, savedMsg }) => (
  <div style={{
    background: '#fff', border: '1px solid #E5E7EB',
    borderRadius: 8, padding: '16px 20px', marginBottom: 16,
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  }}>
    <div style={{ fontSize: 14, fontWeight: 600, color: C.blue, marginBottom: 4 }}>Temperature operating limits</div>
    <div style={{ fontSize: 11, color: C.grey, marginBottom: 12 }}>
      Define min/max for each parameter. Joints outside these limits are flagged as excursions.
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {([
        ['Water temp (°F)',    'waterMin',    'waterMax'],
        ['Base temp (°F)',     'baseMin',     'baseMax'],
        ['Hardener temp (°F)', 'hardenerMin', 'hardenerMax'],
      ] as const).map(([label, minKey, maxKey]) => (
        <div key={label}>
          <div style={{ fontSize: 11, fontWeight: 500, color: C.grey, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 }}>
            {label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number"
              value={draft[minKey]}
              onChange={e => onChange({ ...draft, [minKey]: Number(e.target.value) })}
              style={{ width: 60, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12, textAlign: 'center', color: C.text }}
            />
            <span style={{ fontSize: 11, color: C.grey }}>to</span>
            <input
              type="number"
              value={draft[maxKey]}
              onChange={e => onChange({ ...draft, [maxKey]: Number(e.target.value) })}
              style={{ width: 60, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12, textAlign: 'center', color: C.text }}
            />
          </div>
        </div>
      ))}
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
      <button
        onClick={onApply}
        style={{ padding: '6px 16px', border: 'none', borderRadius: 8, background: C.blue, color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
      >
        Apply limits
      </button>
      <button
        onClick={onClear}
        style={{ padding: '6px 16px', border: `1px solid ${C.border}`, borderRadius: 8, background: C.surface, color: C.text, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
      >
        Clear limits
      </button>
      {savedMsg && <span style={{ fontSize: 11, color: C.green, marginLeft: 8 }}>Limits applied ✓</span>}
    </div>
  </div>
);

// ─── SprayDashboardSection ────────────────────────────────────────────────────

interface SprayDashboardSectionProps {
  rows: CoatingRow[];
  /** Right side of the main title row (e.g. Set limits). */
  headerRight?: React.ReactNode;
  /** Applied temperature limits from the summary toolbar (drives excursion strip in Environmental window). */
  temperatureLimits: TemperatureLimits;
  limitsActive: boolean;
}

const SprayDashboardSection: React.FC<SprayDashboardSectionProps> = ({
  rows,
  headerRight,
  temperatureLimits,
  limitsActive,
}) => {
  const data = useMemo(
    () => computeDashData(rows, DEFAULT_LIMITS, false),
    [rows],
  );
  const { kpis, dailyJoints /* FUTURE: , faults, envData, operators, material */ } = data;

  /** One bar per day: total = pass + fail from `dailyJoints` (same underlying counts). */
  const dailyJointsTotalOnly = useMemo(
    () => dailyJoints.map((d) => ({ date: d.date, total: d.pass + d.fail })),
    [dailyJoints],
  );

  // Keep props in use when limits / env UI is re-enabled on this page.
  void temperatureLimits;
  void limitsActive;

  /* FUTURE: Environmental window — excursion strip when limits are active
  const envExcursion = useMemo(() => {
    if (!limitsActive) return null;
    return environmentalExcursionCopy(rows, temperatureLimits);
  }, [rows, limitsActive, temperatureLimits]);
  */

  return (
    <div style={{ marginBottom: 24 }}>

      {/* ── header ── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: C.blue, letterSpacing: '-0.01em' }}>
            Spray operations overview
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: C.grey }}>
            Computed from loaded session · {kpis.jointsSprayed} joints
          </p>
        </div>
        {headerRight}
      </div>

      {/* ── KPI row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
        <KpiCard
          label="Joints sprayed" value={kpis.jointsSprayed}
          info={{ title: 'Joints Sprayed', description: 'Count of distinct calendar day + joint combinations after the first production joint numbered 1 in each operator session (same trim as session detail). The same joint number on different days counts separately; multiple attempts on the same day and joint count once.', tip: 'Dates are normalized so different cell formats for the same day still merge.' }}
        />
        <KpiCard
          label="First-pass yield"
          value={(
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>{kpis.fpYield}%</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginTop: 6 }}>
                Total yield {kpis.totalYield}%
              </div>
            </div>
          )}
          deltaType={kpis.fpYield >= 95 ? 'up' : kpis.fpYield >= 90 ? 'warn' : 'down'}
          delta={kpis.fpYield >= 95 ? 'On target' : kpis.fpYield >= 90 ? 'Below target' : 'Needs review'}
          info={{
            title: 'First-pass yield & total yield',
            description:
              'First-pass yield: for each calendar day and numeric joint, only the first attempt counts (joints before the first joint numbered 1 in each operator session are excluded, same as session detail). Pass when downstroke and upstroke are Pass, flush is Flush OK, prime is Prime OK, and water temperature is valid (not a sensor fault). Total yield: same buckets; a joint passes if any attempt in that bucket meets those checks.',
            tip: 'A drop in first-pass yield often signals equipment or material issues; total yield shows how often joints eventually succeed after retries.',
          }}
        />
        <KpiCard
          label="Median spray cycle" value={kpis.medianCycle}
          delta="Time between joints" deltaType="neutral"
          info={{ title: 'Median Spray Cycle', description: 'The typical time between finishing one joint and finishing the next. Pauses longer than 5 minutes are treated as idletime and excluded.', tip: "Median is used instead of average so one long break doesn't distort the number." }}
        />
        <KpiCard
          label="Idletime" value={kpis.downtimeStr}
          delta={kpis.gapCount > 0 ? `${kpis.gapCount} gaps > 5 min` : 'No gaps detected'}
          deltaType={kpis.gapCount > 0 ? 'warn' : 'up'}
          info={{ title: 'Idletime', description: 'Sum of gaps longer than 5 minutes between consecutive joints (idletime).', tip: 'Check the joint log to see exactly where each gap occurred.' }}
        />
        <KpiCard
          label="Dew point margin" value={`${kpis.dewMargin}°F`}
          delta={kpis.dewMargin >= 5 ? 'Above 5°F spec' : 'Below 5°F spec'}
          deltaType={kpis.dewMargin >= 5 ? 'up' : 'down'}
          info={{ title: 'Dew Point Margin', description: 'How far above the dew point the ambient temperature is. Coating specifications typically require at least a 5°F margin to prevent moisture condensation.', tip: 'If this drops close to the spec limit, conditions may not be safe for spraying.' }}
        />
      </div>

      {/* ── chart row (was chart + faults; restore second column with gridTemplateColumns: '2fr 1fr' when showing faults) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 16 }}>

        <Card>
          <CardHeader
            title="Joints per day"
            subtitle="Total joints per calendar day"
            info={{ title: 'Joints Per Day', description: 'Daily count of all joint rows in the loaded data (one bar per calendar day).', tip: 'Previously shown as pass vs fail stacks; that split is commented below for reuse.' }}
          />
          {dailyJointsTotalOnly.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={dailyJointsTotalOnly} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="2 3" stroke={C.surf2} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.grey }} axisLine={{ stroke: C.surf2 }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: C.grey }} axisLine={false} tickLine={false} width={28} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="total" fill={C.green} name="Joints" radius={[2, 2, 0, 0]} />
                {/*
                FUTURE: pass / fail stacked bars (use data={dailyJoints} and restore subtitle + info copy)
                <Bar dataKey="pass" stackId="a" fill={C.green} name="Pass" />
                <Bar dataKey="fail" stackId="a" fill={C.red}   name="Fail" radius={[2, 2, 0, 0]} />
                */}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 12, color: C.grey }}>No daily data</span>
            </div>
          )}
        </Card>

        {/* FUTURE: Top fault reasons — add `faults` back to computeDashData destructure; use grid `2fr 1fr` above
        <Card>
          <CardHeader
            title="Top fault reasons"
            info={{ title: 'Top Fault Reasons', description: 'Every fault type in the loaded data, sorted by count (highest first). Joint rows contribute stall / flush / prime buckets; event rows use the device event_message text.', tip: 'Pressure transducer faults are often sensor noise, not actual spray failures.' }}
          />
          <div style={{ height: 180, minHeight: 180, display: 'flex', flexDirection: 'column' }}>
            {faults.length > 0 ? (
              <div
                className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent pr-1"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {faults.map(f => (
                  <div key={f.reason}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, gap: 8 }}>
                      <span style={{ color: C.text, minWidth: 0, wordBreak: 'break-word' }}>{f.reason}</span>
                      <span style={{ color: C.grey, flexShrink: 0 }}>{f.count}</span>
                    </div>
                    <div style={{ height: 5, background: C.surf2, borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(f.count / (faults[0]?.count || 1)) * 100}%`, background: f.color, borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 12, color: C.grey }}>No faults detected</span>
              </div>
            )}
          </div>
        </Card>
        */}
      </div>

      {/* ── FUTURE: env + material + operators row (re-enable when needed on summary) ──
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>

        <Card>
          <CardHeader
            title="Environmental window"
            info={{ title: 'Environmental Window', description: 'The minimum and maximum values recorded for each environmental reading across all joints. This shows the operating range, not averages.', tip: 'Wide ranges may indicate changing weather conditions during the shift.' }}
          />
          {envData.map(r => <KV key={r.label} label={r.label} value={`${r.min}–${r.max}${r.unit}`} />)}
          {limitsActive && envExcursion && (
            <div className="mt-2 pt-2 border-t border-[#E2E5EA]">
              <div className="text-[11px] font-medium text-[#B8860B] flex items-center gap-1 mb-1">
                △ {envExcursion.summary}
              </div>
              {envExcursion.detail ? (
                <div className="text-[11px] text-gray-600">{envExcursion.detail}</div>
              ) : null}
            </div>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Material consumption"
            info={{ title: 'Material Consumption', description: 'Summary of coating material usage — which batches were used and how many joints each batch covered.', tip: 'High variance (>15%) may indicate inconsistent batch quality or operator technique.' }}
          />
          <KV label={material.coatingType || 'Coating'} value={`${material.totalJoints} joints`} />
          <KV label="Base batches"     value={`${material.baseBatches} used`} />
          <KV label="Hardener batches" value={`${material.hardenerBatches} used`} />
          <KV label="Joints per batch" value={material.avgPerBatch} />
        </Card>

        {operators.length > 0 && (
          <Card>
            <CardHeader
              title="Operator leaderboard"
              info={{ title: 'Operator Leaderboard', description: 'Joint count and first-pass yield for each operator in the selected period.', tip: 'This is for identifying coaching opportunities, not ranking people.' }}
            />
            {operators.map(op => (
              <KV key={op.name} label={op.name} value={`${op.joints} · ${op.yield.toFixed(1)}%`} />
            ))}
          </Card>
        )}
      </div>
      */}

    </div>
  );
};

export default SprayDashboardSection;
